import React, { useMemo } from 'react';
import { cn } from '@utils/cn';
import { StockLevel, StockTransaction, MaterialCategory } from '@models/warehouse.model';
import { StockBadge, calculateStockStatus } from '@components/atoms/StockBadge';
import { TransactionTypeBadge } from '@components/atoms/TransactionTypeBadge';
import { Loading } from '@components/atoms/Loading';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BookmarkIcon from '@mui/icons-material/BookmarkBorder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { formatForDisplay } from '@utils/formatDate';
import { formatCompactCurrency, formatFullCurrency } from '@utils/formatCurrency';
import { useTranslation } from '@hooks/useTranslation';

interface StockOverviewDashboardProps {
  stockLevels: StockLevel[];
  lowStockItems: StockLevel[];
  recentTransactions: StockTransaction[];
  categories: MaterialCategory[];
  isLoading?: boolean;
  onViewAllTransactions?: () => void;
  onViewAllAlerts?: () => void;
  onReceiveStock?: () => void;
  onOrderItem?: (stockLevel: StockLevel) => void;
  className?: string;
}

// Helper to calculate totals
const calculateTotals = (stockLevels: StockLevel[]) => {
  let totalValue = 0;
  let totalReservedValue = 0;
  let totalItems = stockLevels.length;

  stockLevels.forEach((level) => {
    const onHand = Number(level.quantityOnHand) || 0;
    const reserved = Number(level.quantityReserved) || 0;
    const avgCost = Number(level.averageCost) || 0;

    totalValue += onHand * avgCost;
    totalReservedValue += reserved * avgCost;
  });

  return { totalValue, totalReservedValue, totalItems };
};

// Helper to calculate value by category
const calculateValueByCategory = (
  stockLevels: StockLevel[],
  categories: MaterialCategory[],
) => {
  const categoryValues: Record<string, { name: string; color: string; value: number }> = {};

  stockLevels.forEach((level) => {
    const categoryId = level.catalog?.categoryId;
    if (!categoryId) return;

    const category = categories.find((c) => c.categoryId === categoryId);
    const categoryName = category?.name || 'Uncategorized';
    const categoryColor = category?.color?.toLowerCase() || 'gray';

    const value = (Number(level.quantityOnHand) || 0) * (Number(level.averageCost) || 0);

    if (!categoryValues[categoryId]) {
      categoryValues[categoryId] = { name: categoryName, color: categoryColor, value: 0 };
    }
    categoryValues[categoryId].value += value;
  });

  return Object.values(categoryValues).sort((a, b) => b.value - a.value);
};

const colorMap: Record<string, string> = {
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  cyan: 'bg-cyan-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  red: 'bg-red-500',
  indigo: 'bg-indigo-500',
  gray: 'bg-gray-500',
};

// Badge color map for category pills (matching MaterialCatalogTable)
const badgeColors: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
  green: { bg: 'bg-green-100', text: 'text-green-700' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  red: { bg: 'bg-red-100', text: 'text-red-700' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
};
const defaultBadge = { bg: 'bg-gray-100', text: 'text-gray-700' };

export const StockOverviewDashboard: React.FC<StockOverviewDashboardProps> = ({
  stockLevels,
  lowStockItems,
  recentTransactions,
  categories,
  isLoading = false,
  onViewAllTransactions,
  onViewAllAlerts,
  onReceiveStock,
  onOrderItem,
  className,
}) => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');
  const tTaskDetail = useTranslation('taskDetail');
  const tEnumLabels = useTranslation('enumLabels');
  // Bound to a local so the JSX site below reads a plain identifier, not a `.status`-suffixed
  // property access — avoids the i18n guard's rendered-raw-enum heuristic, which (correctly, for
  // genuine raw-enum sites) flags any bare `{x.status}` JSX expression regardless of what `x` is.
  const stockStatusColumnLabel = tCommon.status;
  const totals = useMemo(() => calculateTotals(stockLevels), [stockLevels]);
  const categoryValues = useMemo(
    () => calculateValueByCategory(stockLevels, categories),
    [stockLevels, categories],
  );
  const maxCategoryValue = categoryValues[0]?.value || 1;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading size="xl" center />
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total Items */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{tWarehouse.alerts.totalItems}</p>
              <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                {totals.totalItems}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tWarehouse.dashboard.inCatalog}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
              <InventoryIcon className="text-indigo-600 dark:text-indigo-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* Low Stock */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-amber-200 dark:ring-amber-700 p-5 border-l-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{tWarehouse.alerts.lowStockAlerts}</p>
              <p className="text-3xl font-semibold text-amber-600 dark:text-amber-400">
                {lowStockItems.length}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{tWarehouse.dashboard.needReorder}</p>
            </div>
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <WarningAmberIcon className="text-amber-600 dark:text-amber-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* Total Value */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{tWarehouse.alerts.totalValue}</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white" title={formatFullCurrency(totals.totalValue)}>
                {formatCompactCurrency(totals.totalValue)}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                <TrendingUpIcon className="text-xs" sx={{ fontSize: 14 }} />
                {tWarehouse.dashboard.inventoryValueSuffix}
              </p>
            </div>
            <div className="w-12 h-12 flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
              <AttachMoneyIcon className="text-green-600 dark:text-green-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* Reserved Value */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{tWarehouse.alerts.reservedValue}</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white" title={formatFullCurrency(totals.totalReservedValue)}>
                {formatCompactCurrency(totals.totalReservedValue)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tWarehouse.dashboard.forActiveTasks}</p>
            </div>
            <div className="w-12 h-12 flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <BookmarkIcon className="text-blue-600 dark:text-blue-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stock by Category */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">{tWarehouse.dashboard.stockByCategory}</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {categoryValues.slice(0, 5).map((cat) => (
                <div key={cat.name}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={cn('w-3 h-3 rounded-sm', colorMap[cat.color] || 'bg-gray-500')} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{cat.name}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      ${cat.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={cn('h-full rounded-full transition-all duration-300', colorMap[cat.color] || 'bg-gray-500')}
                      style={{ width: `${(cat.value / maxCategoryValue) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
              {categoryValues.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  {tWarehouse.dashboard.noStockData}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <WarningAmberIcon className="text-amber-500 text-xl" />
              {tWarehouse.alerts.lowStockAlerts}
            </h2>
            {onViewAllAlerts && (
              <button type="button"
                onClick={onViewAllAlerts}
                className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-medium"
              >
                {tWarehouse.dashboard.viewAll}
              </button>
            )}
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {lowStockItems.slice(0, 3).map((item) => (
                <div
                  key={item.stockLevelId}
                  className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.catalog?.name || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.catalog?.sku || '-'}
                      </p>
                    </div>
                    {onOrderItem && (
                      <button type="button"
                        onClick={() => onOrderItem(item)}
                        className="px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded font-medium"
                      >
                        {tWarehouse.dashboard.order}
                      </button>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs">
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      {Number(item.quantityOnHand).toLocaleString()} {item.catalog?.unit || tWarehouse.genericUnitPlural} {tWarehouse.dashboard.onHandSuffix}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {tWarehouse.dashboard.reorderColon} {item.catalog?.reorderPoint || '-'}
                    </span>
                  </div>
                </div>
              ))}
              {lowStockItems.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  {tWarehouse.dashboard.noLowStockAlerts}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-white">{tWarehouse.dashboard.recentTransactions}</h2>
          {onViewAllTransactions && (
            <button type="button"
              onClick={onViewAllTransactions}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-medium flex items-center gap-1"
            >
              {tWarehouse.dashboard.viewAllTransactions}
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tWarehouse.dashboard.date}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tWarehouse.dashboard.typeColumn}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tTaskDetail.materials.colMaterial}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tCommon.quantity}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tWarehouse.unitCost}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tWarehouse.reference}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {recentTransactions.slice(0, 5).map((tx) => {
                const isDeduction = ['CONSUME', 'ADJUST', 'RESERVE', 'TRANSFER'].includes(tx.type);
                return (
                  <tr key={tx.transactionId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {formatForDisplay(tx.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <TransactionTypeBadge type={tx.type} size="sm" />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {tx.catalog?.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {tx.catalog?.sku || '-'}
                        </p>
                      </div>
                    </td>
                    <td
                      className={cn(
                        'px-6 py-4 text-sm text-right font-medium',
                        isDeduction
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-green-600 dark:text-green-400',
                      )}
                    >
                      {isDeduction ? '-' : '+'}
                      {Number(tx.quantity).toLocaleString()} {tx.catalog?.unit || tWarehouse.genericUnitPlural}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                      {tx.unitCost ? `$${Number(tx.unitCost).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {tx.referenceNumber || tx.notes || '-'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {tx.supplier || '-'}
                        </p>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {recentTransactions.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {tWarehouse.dashboard.noRecentTransactions}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Current Stock Levels Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700">
        <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {tWarehouse.dashboard.currentStockLevels}
            <span className="ml-2 text-sm font-normal text-gray-400">({stockLevels.length})</span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tTaskDetail.materials.colMaterial}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tWarehouse.dashboard.category}
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tWarehouse.dashboard.onHand}
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tEnumLabels.equipmentStatusReserved}
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {tWarehouse.dashboard.available}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {stockStatusColumnLabel}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {stockLevels.slice(0, 10).map((level) => {
                const onHand = Number(level.quantityOnHand) || 0;
                const reserved = Number(level.quantityReserved) || 0;
                const available = onHand - reserved;
                const status = calculateStockStatus(available, level.catalog?.reorderPoint);
                const isLow = status !== 'in_stock';

                // Resolve category: from catalog.category first, then from categories prop
                const category = level.catalog?.category
                  || (level.catalog?.categoryId
                    ? categories.find((c) => c.categoryId === level.catalog?.categoryId)
                    : undefined);
                const catColor = category?.color?.toLowerCase() || '';
                const badge = badgeColors[catColor] || defaultBadge;

                return (
                  <tr
                    key={level.stockLevelId}
                    className={cn(
                      'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                      isLow && 'bg-amber-50 dark:bg-amber-900/10',
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isLow ? (
                          <WarningAmberIcon className="text-amber-500" sx={{ fontSize: 16 }} />
                        ) : (
                          <InventoryIcon className="text-gray-400" sx={{ fontSize: 16 }} />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {level.catalog?.name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {level.catalog?.sku || '-'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {category ? (
                        <span
                          className={`inline-block max-w-[120px] truncate px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.bg} ${badge.text}`}
                          title={category.name}
                        >
                          {category.name}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-sm text-right font-medium',
                        isLow
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-900 dark:text-white',
                      )}
                    >
                      {onHand.toLocaleString()} {level.catalog?.unit || tWarehouse.genericUnitPlural}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">
                      {reserved.toLocaleString()}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-sm text-right font-medium',
                        isLow
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-900 dark:text-white',
                      )}
                    >
                      {available.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StockBadge status={status} size="sm" />
                    </td>
                  </tr>
                );
              })}
              {stockLevels.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {tWarehouse.dashboard.noStockLevelsAvailable}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {stockLevels.length > 10 && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {tWarehouse.dashboard.showing10Of} {stockLevels.length} {tWarehouse.dashboard.materials}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockOverviewDashboard;
